import { useState, useEffect } from "react";
import axios from "axios";
import { Globe, Users, Calendar, Sparkles } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { CityCard } from "@/components/CityCard";
import { Skeleton } from "@/components/ui/skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${API}/cities`);
        setCities(response.data);
      } catch (error) {
        console.error("Error fetching cities:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, []);

  const features = [
    {
      icon: Globe,
      title: "20 Ciudades",
      description: "Encuentra intercambios en las principales ciudades de habla hispana",
    },
    {
      icon: Users,
      title: "Comunidad Activa",
      description: "Miles de personas practicando idiomas cada semana",
    },
    {
      icon: Calendar,
      title: "Eventos Semanales",
      description: "Calendario actualizado con eventos de lunes a domingo",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <img
          src="https://images.unsplash.com/photo-1543269865-cbf427effbad?q=85&w=1920&auto=format&fit=crop"
          alt="Grupo diverso de amigos conversando"
          className="hero-bg"
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="hero-content animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <Sparkles className="w-4 h-4 text-secondary" />
            <span className="text-white/90 text-sm font-medium">
              Conecta con el mundo
            </span>
          </div>
          
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-4 tracking-tight">
            Encuentra tu intercambio
            <br />
            <span className="text-secondary">de idiomas</span>
          </h1>
          
          <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
            Descubre grupos de Meetup, comunidades de Facebook y eventos semanales 
            en las principales ciudades de habla hispana.
          </p>

          <SearchBar variant="hero" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="features-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex flex-col items-center text-center p-6 rounded-2xl bg-muted/30 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities Grid Section */}
      <section id="ciudades" className="py-16 md:py-24" data-testid="cities-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header text-center mb-12">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-3">
              Explora nuestras ciudades
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Selecciona una ciudad para descubrir los mejores grupos y eventos 
              de intercambio de idiomas.
            </p>
          </div>

          {loading ? (
            <div className="city-grid">
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-[4/3] rounded-2xl" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="city-grid" data-testid="cities-grid">
              {cities.map((city, index) => (
                <div 
                  key={city.id}
                  className="animate-fade-in-up opacity-0"
                  style={{ 
                    animationDelay: `${Math.min(index * 0.05, 0.5)}s`,
                    animationFillMode: 'forwards'
                  }}
                >
                  <CityCard city={city} index={index} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-24 bg-foreground text-white" data-testid="about-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-6">
                ¿Qué es un intercambio de idiomas?
              </h2>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Un intercambio de idiomas es una forma divertida y efectiva de practicar 
                un nuevo idioma mientras ayudas a otros a aprender el tuyo. Imagina 
                conocer gente de todo el mundo en bares, cafeterías o parques de tu ciudad.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                En IntercambiosDeIdioma hemos reunido los mejores grupos y eventos de intercambio 
                en 20 ciudades de España y Latinoamérica. Solo tienes que elegir tu ciudad, 
                encontrar un evento y ¡empezar a practicar!
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 rounded-full bg-white/10 text-sm">
                  Aprende español
                </span>
                <span className="px-4 py-2 rounded-full bg-white/10 text-sm">
                  Practica inglés
                </span>
                <span className="px-4 py-2 rounded-full bg-white/10 text-sm">
                  Conoce gente
                </span>
                <span className="px-4 py-2 rounded-full bg-white/10 text-sm">
                  Gratis
                </span>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?q=85&w=800&auto=format&fit=crop"
                alt="Grupo de amigos en un intercambio de idiomas"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-secondary text-foreground p-4 rounded-xl shadow-lg hidden md:block">
                <p className="font-heading font-bold text-2xl">+50k</p>
                <p className="text-sm">Personas conectadas</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
